import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { Trash2, RotateCcw, AlertCircle } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

export default function TrashAdmin() {
  const [trashedProjects, setTrashedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Confirm dialog states
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const trashRef = collection(db, "workspaces", "ai-apps-dashboard", "trash");

  // Fetch trashed items (only projects, filter out users)
  const fetchTrash = async () => {
    setLoading(true);
    try {
      const q = query(trashRef, orderBy("deletedAt", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Filter out users, only show projects
      const projectsOnly = data.filter((item) => item.itemType !== "user");
      setTrashedProjects(projectsOnly);
    } catch (err) {
      console.error("Error fetching trash:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrash();
  }, []);

  // Open restore confirmation
  const openRestoreConfirm = (item) => {
    setSelectedItem(item);
    setShowRestoreDialog(true);
  };

  // Restore project
  const handleRestore = async () => {
    if (!selectedItem) return;

    try {
      console.log("♻️ Restoring project:", selectedItem.title);
      
      // Add back to projects collection
      const projectsRef = collection(db, "workspaces", "ai-apps-dashboard", "projects");
      const { id, deletedAt, originalCollection, ...projectData } = selectedItem;
      
      await addDoc(projectsRef, {
        ...projectData,
        updatedAt: serverTimestamp(),
      });

      // Remove from trash
      const trashDocRef = doc(db, "workspaces", "ai-apps-dashboard", "aidashtrash", selectedItem.id);
      await deleteDoc(trashDocRef);

      console.log("✅ Project restored successfully");

      // Remove from local state
      setTrashedProjects((prevItems) => prevItems.filter((p) => p.id !== selectedItem.id));
      
      // Reset dialog states
      setShowRestoreDialog(false);
      setSelectedItem(null);
    } catch (err) {
      console.error("❌ Error restoring project:", err);
      console.error("❌ Failed to restore project. Please try again.");
      
      // Reset dialog states even on error
      setShowRestoreDialog(false);
      setSelectedItem(null);
    }
  };

  // Open permanent delete confirmation
  const openDeleteConfirm = (item) => {
    setSelectedItem(item);
    setShowDeleteDialog(true);
  };

  // Permanently delete - HARD DELETE from database
  const handlePermanentDelete = async () => {
    if (!selectedItem) return;

    try {
      console.log("🔥 PERMANENTLY DELETING project from database");
      console.log("📋 Project ID:", selectedItem.id);
      console.log("📋 Project Title:", selectedItem.title);
      console.log("📍 Document Path:", `workspaces/ai-apps-dashboard/trash/${selectedItem.id}`);
      
      // PERMANENTLY DELETE from Firestore trash collection
      const trashDocRef = doc(db, "workspaces", "ai-apps-dashboard", "trash", selectedItem.id);
      
      console.log("⏳ Executing deleteDoc...");
      await deleteDoc(trashDocRef);
      
      console.log("✅ deleteDoc() completed without throwing error");
      
      // Verify deletion by trying to fetch the document
      console.log("🔍 Verifying deletion from Firebase...");
      const { getDoc } = await import("firebase/firestore");
      const verifyDoc = await getDoc(trashDocRef);
      
      if (verifyDoc.exists()) {
        console.error("❌ VERIFICATION FAILED: Document still exists in Firebase!");
        console.error("This means Firebase Security Rules may be blocking the deletion");
        console.error("⚠️ Delete operation failed! The document still exists in Firebase. This is likely a Firebase Security Rules issue. Please check your Firestore rules.");
        
        // Reset dialog states
        setShowDeleteDialog(false);
        setSelectedItem(null);
        return;
      }
      
      console.log("✅ VERIFIED: Document PERMANENTLY DELETED from Firestore database!");
      console.log("✅ This project is now GONE FOREVER - cannot be recovered");
      
      // Remove from local state
      setTrashedProjects((prevItems) => prevItems.filter((p) => p.id !== selectedItem.id));
      
      // Reset dialog states
      setShowDeleteDialog(false);
      setSelectedItem(null);
      
      console.log("✅ UI updated - permanent deletion complete");
      
      // Success message
      console.log(`✅ "${selectedItem.title}" has been permanently deleted from the database!`);
    } catch (err) {
      console.error("❌ ERROR permanently deleting project:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
      console.error("Full error:", err);
      
      if (err.code === 'permission-denied') {
        console.error(`🚫 Permission Denied: Your Firebase Security Rules are blocking this deletion. Please update your Firestore rules to allow delete operations.`);
      } else {
        console.error(`❌ Failed to permanently delete project: ${err.message}`);
      }
      
      // Reset dialog states even on error
      setShowDeleteDialog(false);
      setSelectedItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh] text-gray-600 dark:text-gray-300">
        Loading trash...
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trash2 className="text-red-500" size={32} />
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Trash
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {trashedProjects.length} deleted {trashedProjects.length === 1 ? "item" : "items"}
            </p>
          </div>
        </div>
      </div>

      {/* Alert Box */}
      {trashedProjects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg"
        >
          <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={20} />
          <div className="text-sm text-yellow-800 dark:text-yellow-300">
            <p className="font-medium">Items in trash will be kept temporarily</p>
            <p className="text-xs mt-1">
              You can restore them or permanently delete them. Permanent deletion cannot be undone.
            </p>
          </div>
        </motion.div>
      )}

      {/* Trash Table */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md p-6">
        {trashedProjects.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={64} />
            <p className="text-gray-500 dark:text-gray-400 text-lg">Trash is empty</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Deleted projects will appear here
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Division</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Deleted At</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {trashedProjects.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                  >
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-100">
                      {item.title}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {item.division?.join(", ") || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-sm">
                      {item.deletedAt?.toDate
                        ? new Date(item.deletedAt.toDate()).toLocaleDateString()
                        : "Unknown"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openRestoreConfirm(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          title="Restore"
                        >
                          <RotateCcw size={16} />
                          Restore
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                          title="Delete Permanently"
                        >
                          <Trash2 size={16} />
                          Delete Forever
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restore Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRestoreDialog}
        onClose={() => {
          setShowRestoreDialog(false);
          setSelectedItem(null);
        }}
        onConfirm={handleRestore}
        title="Restore Project?"
        message={`Are you sure you want to restore "${selectedItem?.title}" back to active projects?`}
        confirmText="Restore Project"
        cancelText="Cancel"
        type="info"
      />

      {/* Permanent Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedItem(null);
        }}
        onConfirm={handlePermanentDelete}
        title="Permanently Delete?"
        message={`⚠️ This will PERMANENTLY delete "${selectedItem?.title}". This action cannot be undone!`}
        confirmText="Delete Forever"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}

